import { useRef } from 'react';
import MultiSelect, { MultiSelectAction } from 'components/ui/MultiSelect';
import { DaysOfWeek } from 'enums';
import { DAYS_OF_WEEK_OPTIONS } from 'utils/settings.util';
import { WEEKDAY_DAYS, WEEKEND_DAYS } from 'constants/daysOfWeek';
import { MULTI_SELECT, MULTI_SELECT_QUICK_ACTION_ID } from 'constants/multiSelect';
import { t } from 'services/i18n.service';

type MultiSelectSize = 'xs' | 'sm' | 'md' | 'lg';

interface DaysOfWeekMultiSelectProps {
  value: DaysOfWeek[];
  onChange: (days: DaysOfWeek[]) => void;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  size?: MultiSelectSize;
  className?: string;
  'data-testid'?: string;
  showQuickActions?: boolean;
  visibleSelectedCount?: number;
  btnClassName?: string;
}

const DaysOfWeekMultiSelect = ({
  value,
  onChange,
  label,
  tooltip,
  placeholder,
  size = 'sm',
  className = '!min-w-max',
  'data-testid': dataTestId,
  showQuickActions = true,
  visibleSelectedCount = MULTI_SELECT.DAY_OF_WEEK_OPTIONS_COUNT,
  btnClassName = ''
}: DaysOfWeekMultiSelectProps) => {
  const previousValueRef = useRef<DaysOfWeek[]>(value);

  const handleChange = (nextValues: Array<string | number>) => {
    const nextDays = Array.from(
      new Set(nextValues.map((item) => String(item) as DaysOfWeek))
    );

    previousValueRef.current = nextDays;
    onChange(nextDays);
  };

  const dayOptions = DAYS_OF_WEEK_OPTIONS.map(({ label: labelKey, value }) => ({
    label: t(labelKey),
    value
  }));

  const quickActions: MultiSelectAction[] | undefined = showQuickActions
    ? [
        {
          id: MULTI_SELECT_QUICK_ACTION_ID.WEEKDAYS,
          label: t('days.weekdays'),
          getValues: ({ options }) => {
            const set = new Set(WEEKDAY_DAYS);
            return options
              .filter((opt) => set.has(opt.value as DaysOfWeek))
              .map((opt) => opt.value);
          }
        },
        {
          id: MULTI_SELECT_QUICK_ACTION_ID.WEEKEND,
          label: t('days.weekend'),
          getValues: ({ options }) => {
            const set = new Set(WEEKEND_DAYS);
            return options
              .filter((opt) => set.has(opt.value as DaysOfWeek))
              .map((opt) => opt.value);
          }
        }
      ]
    : undefined;

  const control = (
    <MultiSelect
      options={dayOptions}
      value={value}
      onChange={handleChange}
      size={size}
      placeholder={placeholder}
      visibleSelectedCount={visibleSelectedCount}
      actionButtons={quickActions}
      className={className}
      btnClassName={btnClassName}
    />
  );

  if (!label && !tooltip && !dataTestId && !className) {
    return control;
  }

  return (
    <div className={className} data-testid={dataTestId}>
      {label && (
        <label className='block ~text-[0.65rem]/sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
          {label}
        </label>
      )}
      {control}
      {tooltip && (
        <p className='mt-1 ~text-[0.6rem]/xs text-gray-500 dark:text-gray-400'>
          {tooltip}
        </p>
      )}
    </div>
  );
};

export default DaysOfWeekMultiSelect;
